import React, { useCallback, useEffect, useState, memo } from 'react';
import _get from 'lodash.get';
import {
  TextField,
  Autocomplete,
  Divider,
  Checkbox,
  Paper,
  Box,
  FormControlLabel,
} from '@mui/material';
import { TextFieldProps } from '@mui/material/TextField';
import {
  AutocompleteProps,
  AutocompleteChangeReason,
} from '@mui/material/Autocomplete';
import { FormikProps } from 'formik';
import {
  isObject,
  getValueFromAutocompleteInput,
  arrayToObjByEnumOrId,
} from '../../../lib';
import { useStyles } from './inputs.styles';
import { SelectOption } from '../../../types';

interface AutocompleteInputProps {
  formikProps?: Partial<FormikProps<any>>;
  hasSelectAllOption?: boolean;
  textFieldProps: TextFieldProps & {
    name: string;
    slotProps?: {
      input?: Record<string, any>;
      htmlInput?: Record<string, any>;
      inputLabel?: Record<string, any>;
      [key: string]: any;
    };
  };
  value?: any;
  validationKey?: string | string[];
  autocompleteProps: Omit<
    AutocompleteProps<SelectOption<string | number>, true, false, false>,
    'renderInput'
  > & {
    className?: string;
    style?: React.CSSProperties;
    options: SelectOption<string | number>[];
  };
}

interface RenderAutocompleteInputProps {
  handleSelect: (e: React.SyntheticEvent | null, newInputValue: any) => void;
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  error?: boolean;
  hasSelectAllOption?: boolean;
  helperText?: React.ReactNode;
  value?: any;
  textFieldProps: TextFieldProps & {
    name: string;
    autoComplete?: string;
    slotProps?: {
      input?: Record<string, any>;
      htmlInput?: Record<string, any>;
      inputLabel?: Record<string, any>;
      [key: string]: any;
    };
  };
  autocompleteProps: Omit<
    AutocompleteProps<SelectOption<string | number>, true, false, false>,
    'renderInput'
  > & {
    className?: string;
    style?: React.CSSProperties;
    options: SelectOption<string | number>[];
  };
}

export function AutocompleteInput({
  formikProps,
  hasSelectAllOption = false,
  textFieldProps: _textFieldProps,
  value,
  validationKey,
  ...otherProps
}: AutocompleteInputProps): React.ReactElement {
  const {
    // setValues: formikSetValues,
    handleBlur: formikHandleBlur,
    values: formikValues,
    handleChange: formikHandleChange,
    touched,
    errors,
  } = formikProps || {};

  const { onChange, onBlur, ...textFieldProps } = _textFieldProps;
  const { name } = textFieldProps;

  const handleSelect = useCallback(
    async (e: React.SyntheticEvent | null, newInputValue: any) => {
      const value = getValueFromAutocompleteInput(newInputValue);
      const standardEventObj = { target: { name, value } };
      formikHandleChange && formikHandleChange(standardEventObj);
      onChange &&
        onChange(standardEventObj as React.ChangeEvent<HTMLInputElement>);
    },
    [formikHandleChange, name, onChange],
  );

  const handleBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      formikHandleBlur && formikHandleBlur(e);
      onBlur && onBlur(e);
    },
    [formikHandleBlur, onBlur],
  );

  // On blur touched is set on the name but on submit it's set on the nested object so we need to check both
  const isTouched = !!(
    _get(touched, name as string) || _get(touched, validationKey as string)
  );
  const errorText = _get(errors, (validationKey ?? name) as string) as string;

  return (
    <RenderAutocompleteInput
      onBlur={handleBlur}
      handleSelect={handleSelect}
      error={isTouched && !!errorText}
      hasSelectAllOption={hasSelectAllOption}
      helperText={isTouched ? errorText : undefined}
      textFieldProps={textFieldProps}
      value={value ?? formikValues?.[name]}
      {...otherProps}
    />
  );
}

const RenderAutocompleteInput = memo(function RenderAutocompleteInput({
  handleSelect,
  onBlur,
  error,
  hasSelectAllOption,
  helperText,
  value,
  textFieldProps: _textFieldProps,
  autocompleteProps: _autocompleteProps,
}: RenderAutocompleteInputProps): React.ReactElement {
  const { classes, cx } = useStyles();
  const [optionsObj, setOptionsObj] = useState<
    Record<string | number, SelectOption<string | number>>
  >({});
  const [selectAll, setSelectAll] = useState<boolean>(false);

  const { className, style, options, ...autocompleteProps } =
    _autocompleteProps;
  const {
    name,
    label,
    autoComplete,
    slotProps: _slotProps,
    ...textFieldProps
  } = _textFieldProps;
  const {
    input = {},
    htmlInput = {},
    inputLabel = {},
    ...slotProps
  } = _slotProps || {};

  useEffect(() => {
    if (options.length) {
      setOptionsObj(arrayToObjByEnumOrId(options));
    }
  }, [options]);

  const handleToggleSelectAll = (): void => {
    handleSelect(null, !selectAll ? options : []);
    setSelectAll((cur) => !cur);
  };

  const onChange = (
    e: React.SyntheticEvent,
    value: any,
    reason: AutocompleteChangeReason,
  ): void => {
    if (hasSelectAllOption) {
      if (reason === 'clear' || reason === 'removeOption') {
        setSelectAll(false);
      }
      if (
        reason === 'selectOption' &&
        Array.isArray(value) &&
        value.length === options.length
      ) {
        setSelectAll(true);
      }
    }
    handleSelect(e, value);
  };

  return (
    <Autocomplete
      key={Object.keys(optionsObj).length}
      onChange={onChange}
      value={value}
      onBlur={onBlur}
      isOptionEqualToValue={(
        option: SelectOption<string | number>,
        value: any,
      ) => {
        if (!value) return true; // needed to disable mui warning that empty doesn't match any options

        if (isObject(value)) {
          return option.id === value.id;
        } else if (optionsObj[value]) {
          return option.id + '' === optionsObj[value].id + '';
        }
        return option.id === value;
      }}
      getOptionLabel={(option: any) => {
        if (isObject(option)) {
          return option.name ?? '';
        } else if (optionsObj[option]) {
          return optionsObj[option].name ?? '';
        }
        return option?.toString() ?? '';
      }}
      options={options}
      className={cx(classes.inputRoot, className)}
      style={style}
      slots={{
        paper: (paperProps: {
          children?: React.ReactNode;
          [key: string]: any;
        }) => {
          const { children, ...restPaperProps } = paperProps;
          return (
            <Paper {...restPaperProps}>
              {hasSelectAllOption && (
                <>
                  <Box
                    onMouseDown={(e) => e.preventDefault()} // prevent blur
                    pl={1.5}
                    py={0.5}
                  >
                    <FormControlLabel
                      onClick={(e) => {
                        e.preventDefault(); // prevent blur
                        handleToggleSelectAll();
                      }}
                      label='Select all'
                      control={
                        <Checkbox
                          id='select-all-checkbox'
                          checked={selectAll}
                        />
                      }
                    />
                  </Box>
                  <Divider />
                </>
              )}
              {children}
            </Paper>
          );
        },
      }}
      renderInput={(params) => {
        // We need this to disable browser autocomplete https://stackoverflow.com/a/58922317
        params.inputProps.autoComplete = autoComplete ?? 'off';
        return (
          <TextField
            variant='standard'
            {...params}
            label={label}
            name={name}
            error={error}
            helperText={helperText}
            slotProps={{
              input: {
                ...params.InputProps,
                // endAdornment: params.InputProps.endAdornment,
                ...input,
              },
              htmlInput: {
                ...params.inputProps,
                'data-lpignore': true,
                ...htmlInput,
              },
              inputLabel: { className: classes.label, ...inputLabel },
              ...slotProps,
            }}
            {...textFieldProps}
          />
        );
      }}
      {...autocompleteProps}
    />
  );
});
